<template>
  <div id="app">
    <router-view/>
    <div v-if="pageLoading" class="wap-route-loading" role="status" aria-live="polite">
      <span></span>
      <strong>Loading...</strong>
    </div>
    <paper-download-list
      :visible="downloadListVisible"
      :collapsed="downloadListCollapsed"
      :tasks="downloadTasks"
      :summary="downloadTaskSummary"
      :language="language"
      :can-open-task="canOpenDownloadTask"
      @toggle-collapse="downloadListCollapsed = !downloadListCollapsed"
      @close="closeDownloadList"
      @open-task="openDownloadTask"
      @retry-task="retryDownloadTask"
      @retry-bundle-item="retryDownloadBundleItem"
      @remove-task="removeDownloadTask"
      @panel-height-change="handleDownloadListHeightChange"
    ></paper-download-list>
    <paper-upload-list
      :visible="uploadListVisible"
      :collapsed="uploadListCollapsed"
      :tasks="uploadTasks"
      :summary="uploadTaskSummary"
      :language="language"
      :bottom-offset="uploadListBottomOffset"
      @toggle-collapse="uploadListCollapsed = !uploadListCollapsed"
      @close="closeUploadList"
      @retry-task="retryUploadTask"
      @remove-task="removeUploadTask"
    ></paper-upload-list>
  </div>
</template>

<script>
import PaperDownloadList from '@/wap/components/PaperDownloadList.vue'
import PaperUploadList from '@/wap/components/PaperUploadList.vue'
import downloadCenter from '@/utils/downloadCenter'
import uploadCenter from '@/utils/uploadCenter'
import { getLanguage } from '@/api/auth'

const MOBILE_PAGE_INDICATOR_HANDLE_SELECTOR = [
  '.paper-page-count > span',
  '.daily-preview-count > span',
  '.learning-annotation-preview-count > span',
  '.recent-learning-preview-count > span'
].join(', ')
const MOBILE_PAGE_INDICATOR_CONTROL_SELECTOR = [
  '.paper-page-controls',
  '.daily-preview-controls',
  '.learning-annotation-preview-controls',
  '.recent-learning-preview-controls'
].join(', ')
const MOBILE_PAGE_INDICATOR_DRAG_THRESHOLD = 4
const MOBILE_PAGE_INDICATOR_EDGE_GAP = 8

export default {
  components: {
    PaperDownloadList,
    PaperUploadList
  },
  data () {
    return {
      language: getLanguage(),
      downloadListHeight: 0,
      pageLoading: false,
      mobileTouchState: null,
      mobilePageIndicatorDragState: null,
      mobilePageIndicatorViewportFrame: null
    }
  },
  computed: {
    downloadTasks() {
      return downloadCenter.state.tasks
    },
    downloadListVisible() {
      return downloadCenter.state.visible
    },
    downloadListCollapsed: {
      get() {
        return downloadCenter.state.collapsed
      },
      set(value) {
        downloadCenter.state.collapsed = !!value
      }
    },
    downloadTaskSummary() {
      return downloadCenter.getSummary(this.language)
    },
    uploadTasks() {
      return uploadCenter.state.tasks
    },
    uploadListVisible() {
      return uploadCenter.state.visible
    },
    uploadListCollapsed: {
      get() {
        return uploadCenter.state.collapsed
      },
      set(value) {
        uploadCenter.state.collapsed = !!value
      }
    },
    uploadTaskSummary() {
      return uploadCenter.getSummary(this.language)
    },
    uploadListBottomOffset() {
      if (!this.downloadListVisible || !this.downloadTasks.length) {
        return 0
      }
      if (this.downloadListHeight > 0) {
        return `${this.downloadListHeight}px`
      }
      return this.downloadListCollapsed ? 56 : '30vh'
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      this.pageLoading = window.__viwoodsWapPageLoading === true
      this.finishMobileBootstrapLoading()
      window.addEventListener('viwoods-language-change', this.handleLanguageChange)
      window.addEventListener('viwoods-auth-logout', this.handleAuthLogout)
      window.addEventListener('viwoods-wap-page-loading', this.handleWapPageLoading)
      window.addEventListener('touchstart', this.handleMobileTouchStart, { passive: true, capture: true })
      window.addEventListener('touchmove', this.handleMobileTouchMove, { passive: false, capture: true })
      window.addEventListener('touchend', this.handleMobileTouchEnd, { passive: true, capture: true })
      window.addEventListener('touchcancel', this.handleMobileTouchCancel, { passive: true, capture: true })
      window.addEventListener('mousedown', this.handleMobilePageIndicatorMouseDown, true)
      window.addEventListener('resize', this.handleMobilePageIndicatorViewportChange, { passive: true })
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleMobilePageIndicatorViewportChange, { passive: true })
        window.visualViewport.addEventListener('scroll', this.handleMobilePageIndicatorViewportChange, { passive: true })
      }
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('viwoods-language-change', this.handleLanguageChange)
      window.removeEventListener('viwoods-auth-logout', this.handleAuthLogout)
      window.removeEventListener('viwoods-wap-page-loading', this.handleWapPageLoading)
      window.removeEventListener('touchstart', this.handleMobileTouchStart, true)
      window.removeEventListener('touchmove', this.handleMobileTouchMove, true)
      window.removeEventListener('touchend', this.handleMobileTouchEnd, true)
      window.removeEventListener('touchcancel', this.handleMobileTouchCancel, true)
      window.removeEventListener('mousedown', this.handleMobilePageIndicatorMouseDown, true)
      window.removeEventListener('resize', this.handleMobilePageIndicatorViewportChange)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleMobilePageIndicatorViewportChange)
        window.visualViewport.removeEventListener('scroll', this.handleMobilePageIndicatorViewportChange)
      }
    }
    this.cancelMobilePageIndicatorDrag()
    this.cancelMobilePageIndicatorViewportFrame()
  },
  methods: {
    finishMobileBootstrapLoading() {
      this.$router.onReady(() => {
        this.$nextTick(() => {
          if (typeof window.__viwoodsFinishMobileBootstrapLoading == 'function') {
            window.__viwoodsFinishMobileBootstrapLoading()
          }
        })
      })
    },
    getMobileGestureTarget(event) {
      const target = event && event.target
      if (!target) {
        return null
      }
      return target.nodeType == 1 ? target : target.parentElement
    },
    getMobilePageIndicatorContext(event) {
      const target = this.getMobileGestureTarget(event)
      if (!target || typeof target.closest != 'function') {
        return null
      }
      const handle = target.closest(MOBILE_PAGE_INDICATOR_HANDLE_SELECTOR)
      const control = handle && handle.closest(MOBILE_PAGE_INDICATOR_CONTROL_SELECTOR)
      if (!handle || !control || !control.getBoundingClientRect) {
        return null
      }
      return { handle, control }
    },
    getMobilePageIndicatorOffset(control, property) {
      if (!control || !control.style) {
        return 0
      }
      const value = parseFloat(control.style.getPropertyValue(property))
      return Number.isFinite(value) ? value : 0
    },
    getMobilePageIndicatorTargetOffset(control, axis) {
      if (!control) {
        return 0
      }
      const key = axis == 'x' ? '__viwoodsPageIndicatorTargetX' : '__viwoodsPageIndicatorTargetY'
      const storedValue = Number(control[key])
      if (Number.isFinite(storedValue)) {
        return storedValue
      }
      const property = axis == 'x' ? '--mobile-page-indicator-x' : '--mobile-page-indicator-y'
      const initialValue = this.getMobilePageIndicatorOffset(control, property)
      control[key] = initialValue
      return initialValue
    },
    getMobileTouchByIdentifier(touches, identifier) {
      if (!touches || touches.length == 0) {
        return null
      }
      if (identifier === null || identifier === undefined) {
        return touches.length == 1 ? touches[0] : null
      }
      for (let index = 0; index < touches.length; index += 1) {
        if (touches[index].identifier === identifier) {
          return touches[index]
        }
      }
      return null
    },
    getMobileVisualViewportBounds() {
      const viewport = typeof window !== 'undefined' ? window.visualViewport : null
      const left = viewport ? viewport.offsetLeft : 0
      const top = viewport ? viewport.offsetTop : 0
      const width = viewport ? viewport.width : window.innerWidth
      const height = viewport ? viewport.height : window.innerHeight
      const rootStyles = typeof window !== 'undefined' && typeof window.getComputedStyle == 'function'
        ? window.getComputedStyle(document.documentElement)
        : null
      const safeAreaTop = rootStyles ? parseFloat(rootStyles.getPropertyValue('--viwoods-safe-area-top')) || 0 : 0
      const safeAreaRight = rootStyles ? parseFloat(rootStyles.getPropertyValue('--viwoods-safe-area-right')) || 0 : 0
      const safeAreaBottom = rootStyles ? parseFloat(rootStyles.getPropertyValue('--viwoods-safe-area-bottom')) || 0 : 0
      const safeAreaLeft = rootStyles ? parseFloat(rootStyles.getPropertyValue('--viwoods-safe-area-left')) || 0 : 0
      return {
        left: left + safeAreaLeft + MOBILE_PAGE_INDICATOR_EDGE_GAP,
        top: top + safeAreaTop + MOBILE_PAGE_INDICATOR_EDGE_GAP,
        right: left + width - safeAreaRight - MOBILE_PAGE_INDICATOR_EDGE_GAP,
        bottom: top + height - safeAreaBottom - MOBILE_PAGE_INDICATOR_EDGE_GAP
      }
    },
    positionMobilePageIndicator(control, targetOffsetX, targetOffsetY, rememberTarget = false) {
      if (!control || !control.getBoundingClientRect) {
        return null
      }
      const rect = control.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0 || control.getClientRects().length == 0) {
        return null
      }
      const currentOffsetX = this.getMobilePageIndicatorOffset(control, '--mobile-page-indicator-x')
      const currentOffsetY = this.getMobilePageIndicatorOffset(control, '--mobile-page-indicator-y')
      const baseLeft = rect.left - currentOffsetX
      const baseTop = rect.top - currentOffsetY
      const bounds = this.getMobileVisualViewportBounds()
      let appliedOffsetX = Number.isFinite(Number(targetOffsetX)) ? Number(targetOffsetX) : 0
      let appliedOffsetY = Number.isFinite(Number(targetOffsetY)) ? Number(targetOffsetY) : 0
      const targetLeft = baseLeft + appliedOffsetX
      const targetRight = targetLeft + rect.width
      const targetTop = baseTop + appliedOffsetY
      const targetBottom = targetTop + rect.height
      if (targetLeft < bounds.left) {
        appliedOffsetX += bounds.left - targetLeft
      } else if (targetRight > bounds.right) {
        appliedOffsetX -= targetRight - bounds.right
      }
      if (targetTop < bounds.top) {
        appliedOffsetY += bounds.top - targetTop
      } else if (targetBottom > bounds.bottom) {
        appliedOffsetY -= targetBottom - bounds.bottom
      }
      appliedOffsetX = Math.round(appliedOffsetX)
      appliedOffsetY = Math.round(appliedOffsetY)
      control.style.setProperty('--mobile-page-indicator-x', `${appliedOffsetX}px`)
      control.style.setProperty('--mobile-page-indicator-y', `${appliedOffsetY}px`)
      if (rememberTarget) {
        control.__viwoodsPageIndicatorTargetX = appliedOffsetX
        control.__viwoodsPageIndicatorTargetY = appliedOffsetY
      }
      return { x: appliedOffsetX, y: appliedOffsetY }
    },
    beginMobilePageIndicatorDrag(control, clientX, clientY, source, touchIdentifier = null) {
      if (!control) {
        return false
      }
      this.cancelMobilePageIndicatorDrag()
      // Start from the currently clamped position. The remembered target may be
      // outside a temporarily shrunken visual viewport (for example, a keyboard).
      const currentOffsetX = this.getMobilePageIndicatorOffset(control, '--mobile-page-indicator-x')
      const currentOffsetY = this.getMobilePageIndicatorOffset(control, '--mobile-page-indicator-y')
      this.mobilePageIndicatorDragState = {
        control,
        source,
        touchIdentifier,
        startX: clientX,
        startY: clientY,
        lastX: clientX,
        lastY: clientY,
        startOffsetX: currentOffsetX,
        startOffsetY: currentOffsetY,
        dragging: false
      }
      return true
    },
    startMobilePageIndicatorTouch(event) {
      if (!event || !event.touches || event.touches.length != 1) {
        return false
      }
      const context = this.getMobilePageIndicatorContext(event)
      if (!context) {
        return false
      }
      const touch = event.touches[0]
      return this.beginMobilePageIndicatorDrag(context.control, touch.clientX, touch.clientY, 'touch', touch.identifier)
    },
    moveMobilePageIndicatorDrag(clientX, clientY) {
      const state = this.mobilePageIndicatorDragState
      if (!state || !state.control) {
        return false
      }
      state.lastX = clientX
      state.lastY = clientY
      const deltaX = clientX - state.startX
      const deltaY = clientY - state.startY
      if (!state.dragging && Math.hypot(deltaX, deltaY) < MOBILE_PAGE_INDICATOR_DRAG_THRESHOLD) {
        return true
      }
      if (!state.dragging) {
        state.dragging = true
        state.control.classList.add('mobile-page-indicator-dragging')
      }
      return !!this.positionMobilePageIndicator(
        state.control,
        state.startOffsetX + deltaX,
        state.startOffsetY + deltaY,
        true
      )
    },
    finishMobilePageIndicatorDrag() {
      const state = this.mobilePageIndicatorDragState
      if (state && state.control) {
        state.control.classList.remove('mobile-page-indicator-dragging')
      }
      this.mobilePageIndicatorDragState = null
    },
    cancelMobilePageIndicatorDrag() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousemove', this.handleMobilePageIndicatorMouseMove, true)
        document.removeEventListener('mouseup', this.handleMobilePageIndicatorMouseUp, true)
      }
      this.finishMobilePageIndicatorDrag()
    },
    handleMobilePageIndicatorMouseDown(event) {
      if (!event || event.button !== 0) {
        return
      }
      const context = this.getMobilePageIndicatorContext(event)
      if (!context || !this.beginMobilePageIndicatorDrag(context.control, event.clientX, event.clientY, 'mouse')) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      document.addEventListener('mousemove', this.handleMobilePageIndicatorMouseMove, true)
      document.addEventListener('mouseup', this.handleMobilePageIndicatorMouseUp, true)
    },
    handleMobilePageIndicatorMouseMove(event) {
      const state = this.mobilePageIndicatorDragState
      if (!state || state.source != 'mouse') {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      this.moveMobilePageIndicatorDrag(event.clientX, event.clientY)
    },
    handleMobilePageIndicatorMouseUp(event) {
      const state = this.mobilePageIndicatorDragState
      if (!state || state.source != 'mouse') {
        return
      }
      if (event) {
        this.moveMobilePageIndicatorDrag(event.clientX, event.clientY)
        event.preventDefault()
        event.stopPropagation()
      }
      this.cancelMobilePageIndicatorDrag()
    },
    clampMobilePageIndicator(control) {
      this.positionMobilePageIndicator(
        control,
        this.getMobilePageIndicatorTargetOffset(control, 'x'),
        this.getMobilePageIndicatorTargetOffset(control, 'y')
      )
    },
    clampVisibleMobilePageIndicators() {
      if (!this.$el || !this.$el.querySelectorAll) {
        return
      }
      this.$el.querySelectorAll(MOBILE_PAGE_INDICATOR_CONTROL_SELECTOR).forEach(control => {
        if (!this.mobilePageIndicatorDragState || this.mobilePageIndicatorDragState.control != control) {
          this.clampMobilePageIndicator(control)
        }
      })
    },
    handleMobilePageIndicatorViewportChange() {
      if (this.mobilePageIndicatorViewportFrame !== null) {
        return
      }
      const run = () => {
        this.mobilePageIndicatorViewportFrame = null
        this.clampVisibleMobilePageIndicators()
      }
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame == 'function') {
        this.mobilePageIndicatorViewportFrame = window.requestAnimationFrame(run)
        return
      }
      run()
    },
    cancelMobilePageIndicatorViewportFrame() {
      if (this.mobilePageIndicatorViewportFrame === null) {
        return
      }
      if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame == 'function') {
        window.cancelAnimationFrame(this.mobilePageIndicatorViewportFrame)
      }
      this.mobilePageIndicatorViewportFrame = null
    },
    isProtectedHorizontalGesture(target, clientX) {
      const edgeSize = 28
      const startsAtViewportEdge = clientX <= edgeSize || clientX >= window.innerWidth - edgeSize
      if (startsAtViewportEdge) {
        return true
      }
      // Preview pages own horizontal paging and zoom scrolling. Let their
      // controller consume the gesture instead of blocking it at capture time.
      if (target && typeof target.closest == 'function' && target.closest('.paper-preview-shell, .daily-preview-shell, .learning-annotation-preview-shell, .recent-learning-preview-shell')) {
        return false
      }
      // Keep existing horizontal scrollers (selection/action bars) usable.
      return !this.isNativeHorizontalScrollTarget(target)
    },
    isNativeHorizontalScrollTarget(target) {
      if (!target || typeof document === 'undefined') {
        return false
      }
      let element = target.nodeType == 1 ? target : target.parentElement
      while (element && element !== document.documentElement && element !== document.body) {
        if (element.scrollWidth > element.clientWidth + 1) {
          const styles = typeof window !== 'undefined' && typeof window.getComputedStyle == 'function'
            ? window.getComputedStyle(element)
            : null
          const overflowX = styles ? styles.overflowX : ''
          if (overflowX == 'auto' || overflowX == 'scroll') {
            return true
          }
        }
        element = element.parentElement
      }
      return false
    },
    handleMobileTouchStart(event) {
      if (this.startMobilePageIndicatorTouch(event)) {
        this.mobileTouchState = null
        return
      }
      if (this.mobilePageIndicatorDragState) {
        this.finishMobilePageIndicatorDrag()
      }
      if (!event.touches || event.touches.length != 1) {
        this.mobileTouchState = null
        return
      }
      const target = this.getMobileGestureTarget(event)
      if (!target) {
        return
      }
      const touch = event.touches[0]
      this.mobileTouchState = {
        startX: touch.clientX,
        startY: touch.clientY,
        direction: '',
        horizontalProtected: this.isProtectedHorizontalGesture(target, touch.clientX)
      }
    },
    handleMobileTouchMove(event) {
      const indicatorState = this.mobilePageIndicatorDragState
      if (indicatorState && indicatorState.source == 'touch') {
        const touch = event.touches && event.touches.length == 1
          ? this.getMobileTouchByIdentifier(event.touches, indicatorState.touchIdentifier)
          : null
        if (!touch) {
          this.finishMobilePageIndicatorDrag()
          return
        }
        this.moveMobilePageIndicatorDrag(touch.clientX, touch.clientY)
        if (event.cancelable) {
          event.preventDefault()
        }
        event.stopPropagation()
        return
      }
      const state = this.mobileTouchState
      if (!state || !event.touches || event.touches.length != 1) {
        return
      }
      const touch = event.touches[0]
      const deltaX = touch.clientX - state.startX
      const deltaY = touch.clientY - state.startY
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)
      if (!state.direction && Math.max(absX, absY) >= 8) {
        if (absX > absY * 1.15) {
          state.direction = 'horizontal'
        } else if (absY > absX * 1.15) {
          state.direction = 'vertical'
        }
      }
      if (state.direction == 'horizontal') {
        if (state.horizontalProtected && event.cancelable) {
          event.preventDefault()
        }
      }
    },
    handleMobileTouchEnd(event) {
      const indicatorState = this.mobilePageIndicatorDragState
      if (indicatorState && indicatorState.source == 'touch') {
        const touch = event && this.getMobileTouchByIdentifier(event.changedTouches, indicatorState.touchIdentifier)
        if (!touch) {
          return
        }
        this.moveMobilePageIndicatorDrag(touch.clientX, touch.clientY)
        if (event && typeof event.stopPropagation == 'function') {
          event.stopPropagation()
        }
        this.finishMobilePageIndicatorDrag()
      }
      this.mobileTouchState = null
    },
    handleMobileTouchCancel(event) {
      const indicatorState = this.mobilePageIndicatorDragState
      if (indicatorState && indicatorState.source == 'touch') {
        const changedTouch = event && event.changedTouches && event.changedTouches.length > 0
          ? this.getMobileTouchByIdentifier(event.changedTouches, indicatorState.touchIdentifier)
          : null
        if (event && event.changedTouches && event.changedTouches.length > 0 && !changedTouch) {
          return
        }
        if (event && typeof event.stopPropagation == 'function') {
          event.stopPropagation()
        }
        this.finishMobilePageIndicatorDrag()
      }
      this.mobileTouchState = null
    },
    handleLanguageChange(event) {
      this.language = event && event.detail && event.detail.language ? event.detail.language : getLanguage()
    },
    handleAuthLogout() {
      downloadCenter.closeList()
      uploadCenter.closeList()
    },
    handleWapPageLoading(event) {
      this.pageLoading = !!(event && event.detail && event.detail.visible)
    },
    canOpenDownloadTask(task) {
      return downloadCenter.canOpenTask(task)
    },
    openDownloadTask(task) {
      downloadCenter.openTask(task)
    },
    retryDownloadTask(task) {
      downloadCenter.retryTask(task)
    },
    retryDownloadBundleItem(task, bundleItem) {
      downloadCenter.retryBundleItem(task, bundleItem)
    },
    removeDownloadTask(task) {
      downloadCenter.removeTask(task)
    },
    handleDownloadListHeightChange(height) {
      const value = Number(height)
      const nextHeight = Number.isFinite(value) && value > 0
        ? Math.round(value * 100) / 100
        : 0
      if (this.downloadListHeight == nextHeight) {
        return
      }
      this.downloadListHeight = nextHeight
    },
    closeDownloadList() {
      downloadCenter.closeList()
    },
    retryUploadTask(task) {
      uploadCenter.retryTask(task)
    },
    removeUploadTask(task) {
      uploadCenter.removeTask(task)
    },
    closeUploadList() {
      uploadCenter.closeList()
    }
  }
}
</script>

<style>
:root {
  --viwoods-loading-color: #1F3A2D;
  --viwoods-wap-font-family: "PingFang SC", "PingFangSC-Medium", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  --viwoods-safe-area-top: env(safe-area-inset-top, 0px);
  --viwoods-safe-area-right: env(safe-area-inset-right, 0px);
  --viwoods-safe-area-bottom: env(safe-area-inset-bottom, 0px);
  --viwoods-safe-area-left: env(safe-area-inset-left, 0px);
}

html,
body {
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0;
  background: #f3f2ed;
  overscroll-behavior-x: none;
  overscroll-behavior-y: none;
}

#app {
  min-height: 100vh;
  min-height: 100dvh;
  background: #f3f2ed;
  font-family: var(--viwoods-wap-font-family);
  font-weight: 500;
  font-synthesis: none;
  overscroll-behavior: none;
}

#app .paper-preview-shell,
#app .daily-preview-shell,
#app .learning-annotation-preview-shell,
#app .recent-learning-preview-shell {
  overscroll-behavior-x: contain;
}

.wap-route-loading {
  position: fixed;
  inset: 0;
  z-index: 4500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #f3f2ed;
  color: var(--viwoods-loading-color);
}

.wap-route-loading > span {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(31, 58, 45, .2);
  border-top-color: var(--viwoods-loading-color);
  border-radius: 50% !important;
  box-sizing: border-box;
  animation: viwoods-wap-loading-spin .8s linear infinite;
}

.wap-route-loading > strong {
  font-size: 15px;
  line-height: 22px;
  font-weight: 500;
}

@keyframes viwoods-wap-loading-spin {
  to {
    transform: rotate(360deg);
  }
}

#app .paper-download-format-mask {
  position: fixed;
  inset: 0;
  z-index: 4200;
  background: rgba(0, 0, 0, .38);
}

#app .paper-download-format-modal {
  position: fixed;
  left: 0;
  right: 0;
  top: auto;
  bottom: 0;
  width: 100%;
  height: 313px;
  min-height: 0;
  max-height: 100vh;
  transform: none;
  z-index: 4201;
  padding: 34px 17px 47px;
  box-sizing: border-box;
  background: #fff;
}

#app .paper-download-format-modal header {
  height: 24px;
  flex: 0 0 24px;
  margin: 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
}

#app .paper-download-format-modal header strong {
  font-weight: 600;
}

#app .paper-download-format-modal header button {
  width: 24px;
  height: 24px;
}

#app .paper-download-format-modal header button span {
  width: 14px;
  height: 14px;
}

#app .paper-download-format-modal header button span::before,
#app .paper-download-format-modal header button span::after {
  width: 14px;
  height: 1.5px;
  top: 6px;
}

#app .paper-download-format-modal .paper-download-options {
  min-height: 0;
  flex: 1;
  padding: 29px 8px 0;
  display: flex;
  flex-direction: column;
  row-gap: 30px;
}

#app .paper-download-format-modal .paper-download-options label {
  flex: 0 0 20px;
  gap: 10px;
  font-size: 18px;
  line-height: 24px;
  font-weight: 400;
}

#app .paper-download-format-modal .paper-download-options input {
  appearance: none;
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  margin: 0;
  border: 1px solid #aaa;
  border-radius: 50%;
  box-sizing: border-box;
  background: #fff;
  outline: 0;
}

#app .paper-download-format-modal .paper-download-options input:checked {
  background: radial-gradient(circle at center, #050505 0 6px, #fff 6.5px);
}

#app .paper-download-format-modal footer {
  flex: 0 0 46px;
  gap: 14px;
  margin: 0;
}

#app .paper-download-format-modal footer button {
  min-width: 0;
  height: 46px;
  flex: 1;
  padding: 0;
  border-color: #d5d5d5;
  color: #4b4b4b;
  font-size: 16px;
  font-weight: 600;
  line-height: 44px;
}

#app .paper-download-format-modal footer button:last-child {
  border-color: #1f3a2d;
  background: #1f3a2d;
  color: #fff;
}

button,
input[type="button"],
input[type="submit"],
input[type="reset"],
.ant-btn,
.button,
[class*="-button"],
[class*="_button"],
.switch-login,
.send-code,
.open-setting,
.right_block {
  border-radius: 0 !important;
}

.ant-input:hover:not(:focus):not(.ant-input-focused),
.ant-input-affix-wrapper:hover:not(.ant-input-affix-wrapper-focused),
input:hover:not(:focus),
textarea:hover:not(:focus) {
  border-color: #d9d9d9 !important;
}

#app .ant-spin-nested-loading .ant-spin .ant-spin-dot,
#app .ant-spin-spinning .ant-spin-dot {
  color: var(--viwoods-loading-color) !important;
}

#app .ant-spin-nested-loading .ant-spin .ant-spin-dot .ant-spin-dot-item,
#app .ant-spin-spinning .ant-spin-dot .ant-spin-dot-item {
  background-color: var(--viwoods-loading-color) !important;
}

#app .ant-spin-text {
  color: var(--viwoods-loading-color) !important;
}

#app .paper-preview-stage {
  position: relative;
}

#app .paper-preview-page-image {
  transition: opacity .16s ease;
}

#app .paper-preview-page-image.is-loading {
  opacity: 0;
}

#app .paper-preview-page-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  pointer-events: none;
}

#app .paper-preview-page-loading > .paper-preview-page-loading-spin {
  color: var(--viwoods-loading-color) !important;
}

#app .paper-preview-page-loading > .paper-preview-page-loading-spin .ant-spin-dot {
  position: static;
  margin: 0;
}

#app .paper-upload-input {
  display: none !important;
}

#app .list-bottom-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 0 20px;
  box-sizing: border-box;
  color: #6f6f6f;
  font-size: 13px;
  line-height: 20px;
}

#app .list-bottom-loading .ant-spin {
  color: var(--viwoods-loading-color) !important;
}

#app .cloud-list-spin.ant-spin-nested-loading > div > .ant-spin,
#app .paper-list-spin.ant-spin-nested-loading > div > .ant-spin,
#app .daily-list-spin.ant-spin-nested-loading > div > .ant-spin,
#app .loading-spinner.ant-spin-nested-loading > div > .ant-spin {
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  max-height: none !important;
  padding-top: 0 !important;
  box-sizing: border-box !important;
  display: block !important;
  color: var(--viwoods-loading-color) !important;
}

#app .cloud-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot,
#app .paper-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot,
#app .daily-list-spin.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot,
#app .loading-spinner.ant-spin-nested-loading > div > .ant-spin .ant-spin-dot {
  position: absolute !important;
  top: 33.333% !important;
  left: 50% !important;
  margin: -10px 0 0 -10px !important;
}

#app .paper-password-field button span,
#app .recent-password-field button span {
  display: block !important;
  width: 22px !important;
  height: 22px !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent url("~@/assets/wap/common/password-eye-hidden.png") center / 22px 22px no-repeat !important;
  transform: none !important;
}

#app .paper-password-field button span::before,
#app .paper-password-field button span::after,
#app .recent-password-field button span::before,
#app .recent-password-field button span::after {
  content: '' !important;
  display: none !important;
}

#app .paper-password-field button span.visible,
#app .recent-password-field button span.visible {
  background-image: url("~@/assets/wap/common/password-eye.png") !important;
}

#app .content-area.is-recent-page .recent-cover-default,
#app .content-area.is-global-search .global-search-cover-default {
  width: 30px !important;
  min-width: 30px !important;
  max-width: 30px !important;
  height: 30px !important;
  min-height: 30px !important;
  max-height: 30px !important;
  flex: 0 0 30px !important;
  object-fit: contain !important;
  display: block !important;
  box-sizing: content-box !important;
}

#app .content-area.is-recent-page .recent-row-cover-default,
#app .content-area.is-global-search .global-search-row-cover-default {
  width: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  flex: 0 0 32px !important;
  object-fit: contain !important;
  display: block !important;
  box-sizing: content-box !important;
}

#app .content-area.is-recent-page .recent-row-cover-default.folder-icon,
#app .content-area.is-global-search .global-search-cover-default.folder-child-icon {
  width: 46px !important;
  min-width: 46px !important;
  max-width: 46px !important;
  height: 46px !important;
  min-height: 46px !important;
  max-height: 46px !important;
  flex: 0 0 46px !important;
}

#app .content-area.is-global-search .global-search-cover-default.folder-template,
#app .content-area.is-global-search .global-search-row-cover-default.folder-template {
  width: 42px !important;
  min-width: 42px !important;
  max-width: 42px !important;
  height: 56px !important;
  min-height: 56px !important;
  max-height: 56px !important;
  flex: 0 0 42px !important;
}

#app .content-area.is-global-search .global-search-row-cover-default.folder-child-icon {
  width: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  flex: 0 0 32px !important;
}

#app .paper-row-cover .paper-row-cover-default.encrypt-icon,
#app .global-search-row-cover .global-search-row-cover-default.encrypt-icon {
  width: 46px !important;
  height: 62px !important;
  border: 1px solid #d7d9d6 !important;
  background-color: #fff !important;
  box-sizing: border-box !important;
  object-fit: contain !important;
}

#app .paper-row-cover.locked,
#app .global-search-row-cover.locked {
  height: 62px !important;
  overflow: visible !important;
}

#app .content-area.is-global-search .global-search-row-cover.locked {
  width: 43px !important;
  height: 58px !important;
  border: 1px solid #d7d9d8 !important;
  border-radius: 3px !important;
  background: #fff !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
}

#app .content-area.is-global-search .global-search-row.selected .global-search-row-cover.locked {
  border-color: transparent !important;
}

#app .content-area.is-global-search .global-search-row-cover.locked .global-search-row-cover-default.encrypt-icon {
  width: 22px !important;
  height: 22px !important;
  max-height: none !important;
  border: 0 !important;
  background: transparent !important;
  box-sizing: content-box !important;
  object-fit: contain !important;
}

#app .content-area.is-recent-page .recent-row-cover.locked {
  width: 43px !important;
  height: 58px !important;
  border: 1px solid #d7d9d8 !important;
  border-radius: 3px !important;
  background: #fff !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
}

#app .content-area.is-recent-page .recent-row.selected .recent-row-cover.locked {
  border-color: transparent !important;
}

#app .content-area.is-recent-page .recent-row-cover.locked .recent-row-cover-default.encrypt-icon {
  width: 22px !important;
  height: 22px !important;
  max-height: none !important;
  border: 0 !important;
  background: transparent !important;
  box-sizing: content-box !important;
  object-fit: contain !important;
}

#app .paper-row.selected,
#app .recent-row.selected,
#app .global-search-row.selected {
  background: #DDE4D9 !important;
  background-color: #DDE4D9 !important;
}

#app .paper-card,
#app .paper-row,
#app .recent-card,
#app .recent-row,
#app .global-search-card,
#app .global-search-row,
#app .daily-card,
#app .daily-row {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}

#app .paper-card img,
#app .paper-row img,
#app .recent-card img,
#app .recent-row img,
#app .global-search-card img,
#app .global-search-row img,
#app .daily-card img,
#app .daily-row img {
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
  user-select: none;
  pointer-events: none;
}

#app .mobile-preview-grid-item {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

#app .mobile-preview-grid-item img {
  -webkit-touch-callout: none;
  -webkit-user-drag: none;
  user-select: none;
  pointer-events: none;
}

#app .paper-tool-button,
#app .daily-preview-tool,
#app .learning-annotation-preview-tool,
#app .recent-learning-preview-tool {
  position: relative !important;
  overflow: visible !important;
}

#app .preview-zoom-tip {
  position: absolute;
  top: calc(100% + 13px);
  left: 50%;
  z-index: 200;
  min-width: 118px;
  height: 45px;
  padding: 0 18px;
  box-sizing: border-box;
  transform: translateX(-50%);
  border: 1px solid #d8d8d8;
  background: #fff;
  color: #202428;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  line-height: 17px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 10px rgba(0, 0, 0, .04);
}

#app .paper-tool-button:disabled,
#app .daily-preview-tool:disabled,
#app .learning-annotation-preview-tool:disabled,
#app .recent-learning-preview-tool:disabled {
  opacity: 1 !important;
}

#app .paper-tool-button:disabled > img,
#app .daily-preview-tool:disabled > img,
#app .learning-annotation-preview-tool:disabled > img,
#app .recent-learning-preview-tool:disabled > img {
  opacity: .35 !important;
}

#app .paper-filter,
#app .paper-sort,
#app .daily-filter-wrap {
  flex: 0 0 auto !important;
  max-width: calc(100vw - 72px) !important;
}

#app .paper-filter-button,
#app .paper-sort-button,
#app .daily-filter-button {
  width: auto !important;
  min-width: 118px !important;
  max-width: calc(100vw - 72px) !important;
  white-space: nowrap !important;
}

#app .paper-filter-button span:not(.filter-arrow),
#app .paper-sort-button,
#app .daily-filter-button em {
  flex: 0 1 auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

#app .paper-filter-menu,
#app .paper-sort-menu,
#app .daily-filter-menu {
  width: max-content !important;
  min-width: 100% !important;
  max-width: calc(100vw - 72px) !important;
}

#app .paper-filter-menu button,
#app .paper-sort-menu button,
#app .daily-filter-menu button {
  width: max-content !important;
  min-width: 100% !important;
  white-space: nowrap !important;
}

#app .paper-filter-menu button span,
#app .paper-sort-menu button span,
#app .daily-filter-menu button span {
  flex: 0 1 auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}

body .cloud-setting-modal .ant-spin-spinning .ant-spin-dot,
body .cloud-setting-modal .ant-spin-nested-loading .ant-spin .ant-spin-dot {
  color: var(--viwoods-loading-color) !important;
}

body .cloud-setting-modal .ant-spin-spinning .ant-spin-dot .ant-spin-dot-item,
body .cloud-setting-modal .ant-spin-nested-loading .ant-spin .ant-spin-dot .ant-spin-dot-item {
  background-color: var(--viwoods-loading-color) !important;
}

html body .custom-loading .el-loading-spinner .path,
html body .el-loading-spinner .path {
  stroke: var(--viwoods-loading-color) !important;
}

html body .custom-loading .el-loading-spinner .circular,
html body .el-loading-spinner .circular {
  color: var(--viwoods-loading-color) !important;
}

html body .custom-loading .el-loading-spinner > div,
html body .el-loading-spinner > div {
  border-color: var(--viwoods-loading-color) transparent transparent transparent !important;
}

html body .custom-loading .el-loading-text,
html body .el-loading-text,
html body .el-icon-loading {
  color: var(--viwoods-loading-color) !important;
}

.is-interaction-locked {
  position: relative;
}

.is-interaction-locked::after {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: transparent;
  cursor: wait;
  pointer-events: auto;
  touch-action: none;
}

/* Mobile preview geometry is shared by every WAP detail surface. */
#app .paper-detail-body:not(.pages-open):not(.is-resolving):not(.is-knowledge-ai-note-preview) {
  --paper-preview-base-width: 100vw !important;
  --paper-preview-base-height: 133.333333vw !important;
}

#app .paper-detail-body.is-knowledge-ai-note-preview:not(.pages-open):not(.is-resolving) {
  --paper-preview-base-width: 100vw !important;
  --paper-preview-base-height: calc(100vh - 64px) !important;
  --paper-preview-base-height: calc(100dvh - 64px) !important;
}

#app .cloud-main.is-recent-detail .paper-detail-body.is-knowledge-ai-note-preview:not(.pages-open):not(.is-resolving),
#app .cloud-main.is-search-file-preview-detail .paper-detail-body.is-knowledge-ai-note-preview:not(.pages-open):not(.is-resolving) {
  --paper-preview-base-height: calc(100vh - 55px) !important;
  --paper-preview-base-height: calc(100dvh - 55px) !important;
}

#app .daily-preview-body:not(.is-resolving) {
  --daily-preview-base-width: 100vw !important;
  --daily-preview-base-height: 133.333333vw !important;
}

#app .learning-annotation-preview-body:not(.pages-open) {
  --learning-annotation-preview-base-width: 100vw !important;
  --learning-annotation-preview-base-height: 133.333333vw !important;
}

#app .recent-learning-preview-body:not(.pages-open) {
  --recent-learning-preview-base-width: 100vw !important;
  --recent-learning-preview-base-height: 133.333333vw !important;
}

#app .paper-preview-shell,
#app .daily-preview-shell,
#app .learning-annotation-preview-shell,
#app .recent-learning-preview-shell {
  width: 100% !important;
  height: 100% !important;
  min-height: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  overflow: auto !important;
  box-sizing: border-box !important;
  touch-action: none;
  overscroll-behavior: contain;
}

#app .paper-preview-zoom,
#app .daily-preview-zoom,
#app .learning-annotation-preview-zoom,
#app .recent-learning-preview-zoom {
  flex: 0 0 auto !important;
  margin: auto !important;
}

#app .paper-page-controls,
#app .daily-preview-controls,
#app .learning-annotation-preview-controls,
#app .recent-learning-preview-controls {
  --mobile-page-indicator-x: 0px;
  --mobile-page-indicator-y: 0px;
  position: fixed !important;
  left: 50% !important;
  right: auto !important;
  top: auto !important;
  bottom: calc(10px + constant(safe-area-inset-bottom)) !important;
  bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
  z-index: 50 !important;
  align-items: center !important;
  justify-content: center !important;
  transform: translate3d(calc(-50% + var(--mobile-page-indicator-x)), var(--mobile-page-indicator-y), 0) !important;
  pointer-events: none !important;
  will-change: transform;
}

#app .paper-page-count,
#app .daily-preview-count,
#app .learning-annotation-preview-count,
#app .recent-learning-preview-count {
  pointer-events: auto !important;
}

#app .paper-page-count > span,
#app .daily-preview-count > span,
#app .learning-annotation-preview-count > span,
#app .recent-learning-preview-count > span {
  height: 35px !important;
  min-height: 35px !important;
  max-height: 35px !important;
  box-sizing: border-box !important;
  cursor: grab;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

#app .mobile-page-indicator-dragging .paper-page-count > span,
#app .mobile-page-indicator-dragging .daily-preview-count > span,
#app .mobile-page-indicator-dragging .learning-annotation-preview-count > span,
#app .mobile-page-indicator-dragging .recent-learning-preview-count > span {
  cursor: grabbing;
}
</style>
